import React, { useState, useCallback, useEffect, useRef } from "react";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import MarketSaleRowPro from "../MarketSaleRowPro/MarketSaleRowPro";
import ErrorDialog from "../ErrorDialog";
import ReviewDialog from "../ReviewDialog";
import { AddMiniTableProperty } from "services/miniTablePro";
import { showErrorNotification } from "common/helpers";
import { withSnackbar } from "notistack";
import { compose } from "recompose";
import {
  isPropertyAgentListing,
  deriveFullAddressFromPropertyKeys,
  getPropertyTypeOptions,
  requiresStreetNumberOverrideForProperty,
} from "../utils";
import {
  STATUS_OPTIONS,
  ADDRESS_MAX_LENGTH,
  CUSTOM_PROPERTY_TYPE_MAX_LENGTH,
  DIALOG_BUTTON_BORDER_RADIUS,
  DIALOG_BUTTON_MIN_HEIGHT,
  MAX_LIST_SALE_PRICE,
} from "../constants";
import useStyles from "./styles";
import { getSaleDateMaxForInput, validateSinglePropertyForm } from "./validations";
import { formatYmdFromSaleDateValue, parseSaleDateLocal } from "./saleDateInputUtils";
import { clampBedsFullHalfInt, formatBedBathsString, parseBedBathsString } from "../bedBathsUtils";

function applyMaxListSalePrice(n) {
  return Math.min(Math.trunc(n), MAX_LIST_SALE_PRICE);
}

/** Digit-only price string → stored value capped at MAX_LIST_SALE_PRICE */
function sanitizeListSalePriceDigits(rawDigits) {
  if (rawDigits === "") return "";
  const n = Number(rawDigits);
  if (Number.isNaN(n)) return "";
  return String(applyMaxListSalePrice(n));
}

/** Load from API / property object — cap so form never shows above max */
function clampPriceFromProperty(val) {
  if (val === undefined || val === null || val === "" || val === "-") return "";
  const n = Number(String(val).replace(/,/g, ""));
  if (Number.isNaN(n)) return "";
  return String(applyMaxListSalePrice(n));
}

/**
 * SinglePropertyAddEditDialog - Dialog for adding or editing a single property
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.onClose - Called when dialog closes
 * @param {string} props.areaName - Area name to display in header
 * @param {Object} props.property - Property to edit (null/undefined for add mode)
 * @param {Object} props.agentData - Agent data (contains listgenUserID)
 * @param {string} props.mlsAreaID - MLS Area ID
 * @param {function} props.onSave - Called when form is saved, receives edited property data
 * @param {string} props.editedText - Text to show when property is edited (default: "You EDITED This property")
 * @param {string} props.addedText - Text to show when property is added (default: "You ADDED This property")
 * @param {Object} props.marketSaleRowProps - Props to pass to MarketSaleRowPro
 */
function SinglePropertyAddEditDialog({
  open,
  onClose,
  areaName = "",
  property = null,
  agentData = {},
  mlsAreaID = "",
  listgenUserAreaID = "",
  miniTableMeta = {},
  onSave,
  editedText = "You EDITED This Property",
  addedText = "You ADDED This Property",
  marketSaleRowProps = {},
}) {
  const classes = useStyles();
  const isEditMode = property !== null && property !== undefined;
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const saleDateInputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pendingProperty, setPendingProperty] = useState(null);
  const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
  const [validationErrorMessages, setValidationErrorMessages] = useState([]);
  const [showStreetNumberOverrideDialog, setShowStreetNumberOverrideDialog] = useState(false);
  const [streetNumberOverride, setStreetNumberOverride] = useState(false);

  const normalizeStreetNumber = (value) => {
    const normalized = String(value ?? "").trim();
    return normalized === "-" ? "" : normalized;
  };
  const oldStreetNumber = normalizeStreetNumber(property?.streetNumber);

  const memoizedOldRow = React.useMemo(() => {
    if (!isEditMode || !property) return null;
    return (
      <MarketSaleRowPro
        properties={[property]}
        asSingleRow={true}
        hidePropertyTypeColumn={false}
        hasAllActiveProperties={false}
        {...marketSaleRowProps}
        hidePropertyBar={true}
      />
    );
  }, [isEditMode, property, marketSaleRowProps]);

  // Check if property is an agent property (warning only when NOT agent property; isAgentFiltered false = co-listing)
  const isAgentProperty = useCallback(() => {
    if (!property) return true; // For add mode, assume it's allowed
    return isPropertyAgentListing(property);
  }, [property]);

  /**
   * Simple override rule:
   * - For Active/Pending/Contingent/ActiveContingent, street number requires override approval before typing.
   * - For other statuses, street number is directly editable.
   */
  const isStreetNumberReadOnly = (status, streetNumberOverride, isAgentFiltered) =>
    requiresStreetNumberOverrideForProperty({ status, isAgentFiltered }) &&
    !streetNumberOverride;

  /** Value to set for street number when switching to co-listing: edit = original property value, add = "". */
  const getStreetNumberResetValue = () => {
    if (!isEditMode || !property) return "";//Add mode
    const raw = String(property.streetNumber ?? "").trim();
    return raw === "-" ? "" : raw;
  };

  // Initialize form data when dialog opens or property changes
  useEffect(() => {
    if (open) {
      setIsSaving(false);
      setFieldErrors({});
      setSubmitAttempted(false);
      setStreetNumberOverride(false);
      if (isEditMode && property) {
        const typeOptions = getPropertyTypeOptions();
        const { propertyTypeKey, customPropertyType } = (() => {
          const raw = (property.propertyType || "").trim();
          const match = typeOptions.find(
            (opt) => opt.key === raw || opt.label.toLowerCase() === raw.toLowerCase()
          );
          return {
            propertyTypeKey: match ? match.key : "",
            customPropertyType: match ? "" : raw.slice(0, CUSTOM_PROPERTY_TYPE_MAX_LENGTH),
          };
        })();
        const baths = parseBedBathsString(property.bedBaths || property.baths || "");
        setFormData({
          status: property.status || "",
          saleDate: property.saleDate ? formatDateForInput(property.saleDate) : "",
          propertyType: propertyTypeKey || "",
          customPropertyType,
          // Dir/Suffix are intentionally not used for now
          streetDirPrefix: "",
          streetNumber: (() => {
            const v = (property.streetNumber ?? "").toString().trim();
            return v === "-" ? "" : v;
          })(),
          streetName: (() => {
            const v = (property.streetName ?? "").toString().trim();
            return v === "-" ? "" : v;
          })(),
          streetSuffix: "",
          listPrice: clampPriceFromProperty(property.listPrice),
          salePrice: clampPriceFromProperty(property.salePrice),
          daysOnMarket:
            property.status === "Coming Soon" ? "" : formatNumForInput(property.daysOnMarket),
          beds: formatNumForInput(property.beds ?? baths.beds),
          fullBath: formatNumForInput(baths.full),
          halfBath: formatNumForInput(baths.half),
          livingSqft: formatNumForInput(property.livingSqft ?? property.squareFeet),
          isAgentFiltered: property.isAgentFiltered === true,
        });
      } else {
        const typeOptions = getPropertyTypeOptions();
        const defaultPropertyType = typeOptions.length > 0 ? typeOptions[0].key : "";
        setFormData({
          status: STATUS_OPTIONS[0]?.value ?? "Active",
          saleDate: "",
          propertyType: defaultPropertyType,
          customPropertyType: "",
          streetDirPrefix: "",
          streetNumber: "",
          streetName: "",
          streetSuffix: "",
          listPrice: "",
          salePrice: "",
          daysOnMarket: "",
          beds: "",
          fullBath: "",
          halfBath: "",
          livingSqft: "",
          isAgentFiltered: false,
        });
      }
    }
  }, [open, isEditMode, property]);

  function formatDateForInput(val) {
    return formatYmdFromSaleDateValue(val);
  }
  function formatDateForTextBox(val) {
    const d = parseSaleDateLocal(val);
    if (!d) return "";
    try {
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      });
    } catch {
      return "";
    }
  }
  function formatNumForInput(val) {
    if (val === undefined || val === null || val === "") return "";
    if (val === "-") return "";
    return String(val);
  }

  const handleWarningProceed = () => {
    setShowWarningDialog(false);
  };

  const handleWarningCancel = () => {
    setShowWarningDialog(false);
    onClose?.(); // Close everything if user cancels warning
  };

  // Check if we should show warning when dialog opens
  React.useEffect(() => {
    if (open && isEditMode && property && !isAgentProperty()) {
      setShowWarningDialog(true);
    } else {
      setShowWarningDialog(false);
    }
  }, [open, isEditMode, property, isAgentProperty]);
  const shouldOpenEditDialog = open && !showWarningDialog;

  const handleSave = () => {
    const errors = validateSinglePropertyForm(formData);
    setFieldErrors(errors);
    setSubmitAttempted(true);
    if (Object.keys(errors).length > 0) {
      setValidationErrorMessages(Object.values(errors));
      setShowValidationErrorDialog(true);
      return;
    }

    const nextStreetNumber = normalizeStreetNumber(formData.streetNumber);
    const streetNumberChangedFromOld = nextStreetNumber !== oldStreetNumber;
    const requiresOverrideAtSubmit = requiresStreetNumberOverrideForProperty({
      status: formData.status,
      isAgentFiltered: formData.isAgentFiltered,
    });
    if (
      requiresOverrideAtSubmit &&
      !streetNumberOverride &&
      nextStreetNumber !== "" &&
      streetNumberChangedFromOld
    ) {
      setShowStreetNumberOverrideDialog(true);
      return;
    }

    // Show street number only when street number is not empty, isAgentFiltered is true or (requires override, and street number override is true)
    // Otherwise, hide street number for Active/Pending/Contingent/ActiveContingent statuses
    const nextIsShowStreetNumber =
      nextStreetNumber !== "" &&
      (formData.isAgentFiltered === true ||
        (requiresOverrideAtSubmit && streetNumberOverride));
    if (nextIsShowStreetNumber) {
      setFormData((prev) => ({ ...prev, _isShowStreetNumber: true }));
    } else {
      setFormData((prev) => ({ ...prev, _isShowStreetNumber: false }));
    }

    if (!onSave) return;
    const nowIso = new Date().toISOString().slice(0, 19);
    const saleDateVal = formData.saleDate ? `${formData.saleDate}T00:00:00` : "";
    // API persists `bedBaths` only; do not send duplicate `baths` or carry it from spread
    const { baths: _legacyBaths, ...propertyWithoutBaths } = property || {};
    const updatedProperty = {
      ...propertyWithoutBaths,
      status: formData.status,
      saleDate: saleDateVal,
      propertyType: (formData.customPropertyType || "").trim() || formData.propertyType,
      address: "-",
      streetDirPrefix: null,
      streetNumber: (formData.streetNumber || "").trim() || null,
      _isShowStreetNumber: nextIsShowStreetNumber,
      streetName: (formData.streetName || "").trim() || null,
      streetSuffix: null,
      listPrice: formData.listPrice ? String(formData.listPrice).replace(/,/g, "") : "",
      salePrice: formData.salePrice ? String(formData.salePrice).replace(/,/g, "") : "",
      daysOnMarket: formData.daysOnMarket || "",
      beds: formData.beds || "",
      bedBaths: formatBedBathsString(formData.beds, formData.fullBath, formData.halfBath),
      livingSqft: formData.livingSqft || "",
      squareFeet: formData.livingSqft || "",
      isAgentFiltered: formData.isAgentFiltered === true,
      _isEdited: isEditMode,
      _isManuallyAdded: isEditMode ? !!property?._isManuallyAdded : true,
      _isAdded: isEditMode ? !!property?._isAdded : false,
      _addedDateTime: !isEditMode ? nowIso : (property?._addedDateTime || nowIso),
      _editedDateTime: isEditMode ? nowIso : "",
    };
    setPendingProperty(updatedProperty);
    setReviewOpen(true);
  };

  const handleCancel = () => {
    setFormData({});
    onClose?.();
  };

  const handleReviewConfirm = () => {
    if (!pendingProperty) return;
    setIsSaving(true);
    if (!isEditMode) {
      // Add property: use add mini table API only
      AddMiniTableProperty({
        ...miniTableMeta,
        property: pendingProperty,
      })
        .then((savedProperty) => {
          console.log('savedProperty', savedProperty);
          if (!savedProperty?.property) {
            console.error('No property to save');
            return;
          };
          onSave(savedProperty?.property);
          setReviewOpen(false);
          onClose?.();
        })
        .catch(() => {
          showErrorNotification(props, "Something went wrong while processing your request.");
        })
        .finally(() => setIsSaving(false));
      return;
    }
    // Edit property: parent (MiniTableProEditor) will call update API with full list
    onSave(pendingProperty);
    setReviewOpen(false);
    onClose?.();
  };

  const handleInputChange = (field, value) => {
    const switchingToCoListing = field === "isAgentFiltered" && value === false;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "status" && value !== "Sold") {
        next.saleDate = "";
        next.salePrice = "";
      }
      if (field === "status" && value === "Coming Soon") {
        next.daysOnMarket = "";
      }
      if (switchingToCoListing) {
        next.streetNumber = getStreetNumberResetValue();
      }
      return next;
    });
    if (switchingToCoListing) setStreetNumberOverride(false);
    if (submitAttempted) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        if (next[field]) delete next[field];
        if (
          next.addressCombined &&
          ["streetName", "streetNumber", "streetDirPrefix", "streetSuffix"].includes(field)
        ) {
          delete next.addressCombined;
        }
        return next;
      });
    }
  };

  const handleYourListingClick = () => {
    if (formData.isAgentFiltered) {
      setFormData((prev) => ({ ...prev, isAgentFiltered: false }));
      return;
    }
    setFormData((prev) => ({ ...prev, isAgentFiltered: true }));
  };

  const handleCoListingClick = () => {
    if (!formData.isAgentFiltered) return;
    handleInputChange("isAgentFiltered", false);
  };

  // Get property address for header: from property in edit mode, from form's 4 keys in add mode
  const getPropertyAddress = () => {
    if (isEditMode && property) return deriveFullAddressFromPropertyKeys(property);
    return deriveFullAddressFromPropertyKeys({
      streetDirPrefix: formData.streetDirPrefix,
      streetNumber: formData.streetNumber,
      streetName: formData.streetName,
      streetSuffix: formData.streetSuffix,
    });
  };

  const fullAddressCombined = deriveFullAddressFromPropertyKeys({
    streetDirPrefix: formData.streetDirPrefix,
    streetNumber: formData.streetNumber,
    streetName: formData.streetName,
    streetSuffix: formData.streetSuffix,
  });
  const customPropertyTypeLength = (formData.customPropertyType || "").length;
  const addressOverLimit = fullAddressCombined.length > ADDRESS_MAX_LENGTH;
  const addressUsageMessage = `You have used ${fullAddressCombined.length} characters out of ${ADDRESS_MAX_LENGTH} maximum allowed characters.`;
  const err = (key) => submitAttempted && fieldErrors[key];
  const streetNumberHasError = addressOverLimit || !!err("addressCombined");
  const streetNameHasError =
    addressOverLimit || !!err("streetName") || !!err("addressCombined");
  const bathHasError = !!(err("fullBath") || err("halfBath"));

  return (
    <>
      <ReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onEdit={() => {setReviewOpen(false); setPendingProperty(null);}}
        areaName={areaName}
        properties={pendingProperty ? [pendingProperty] : []}
        onConfirm={handleReviewConfirm}
        marketSaleRowProps={marketSaleRowProps}
        headerLine1={
          isEditMode
            ? "You are <u>EDITING</u> the following property."
            : "You are <u>ADDING</u> the following property."
        }
        headerLine2="Please DOUBLE CHECK that the details are correct."
        updateButtonText="OK — Everything Looks Correct — UPDATE"
        dialogProps={{ style: { zIndex: 3000 } }}
      />
      {/* Warning Dialog for non-agent properties (only when NOT agent property) */}
      <ErrorDialog
        open={showWarningDialog}
        onClose={handleWarningCancel}
        headerText="PLEASE NOTE:"
        contentText={
          "Our records show this is not your listing.\nAre you sure you want to make these changes?"
        }
        headerVariant="blue"
        footerSplit
        buttons={[
          { label: "Cancel", onClick: handleWarningCancel },
          { label: "YES, Proceed", onClick: handleWarningProceed, primary: true },
        ]}
      />

      <ErrorDialog
        open={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
        headerText="ERROR: Please note.."
        contentText={validationErrorMessages.length > 0 ? validationErrorMessages.map((msg) => `• ${msg}`).join("\n") : ""}
        headerVariant="red"
        dialogProps={{ style: { zIndex: 3000 } }}
      />

      <ErrorDialog
        open={showStreetNumberOverrideDialog}
        onClose={() => setShowStreetNumberOverrideDialog(false)}
        paperMaxWidth={560}
        headerText="Warning: Please note.."
        content={
          <>
            According to MLS records, this property is listed by another agent. MLS rules do not allow{" "}
            <strong>STREET NUMBERS</strong> to be displayed on <strong>ACTIVE or PENDING</strong> properties. <br /><br />If you
            want to take <strong>personal responsibility</strong> to show the street number, click{" "}
            <strong>I APPROVE - OVERRIDE MLS RULES</strong> or if you do not want to show the street number,{" "}
            <strong>do not add the street number to this property - Clear Street Number.</strong>
          </>
        }
        headerVariant="red"
        footerSplit
        buttons={[
          {
            label: "Clear Street Number",
            onClick: () => {
              setShowStreetNumberOverrideDialog(false);
              setFormData((prev) => ({ ...prev, streetNumber: "" }));
            },
          },
          {
            label: "I APPROVE - OVERRIDE MLS RULES",
            onClick: () => {
              setShowStreetNumberOverrideDialog(false);
              setStreetNumberOverride(true);
            },
            primary: true,
          },
        ]}
        dialogProps={{ style: { zIndex: 3000 } }}
      />

      {/* Main Edit/Add Dialog - Only show if not showing warning */}
      <MailProDialog
        open={shouldOpenEditDialog}
        onClose={() => { if (!isSaving) handleCancel(); }}
        disableEscapeKeyDown={isSaving}
        classes={{ paper: classes.dialogPaper }}
      >
        <MailProDialogHeader>
          <div className={classes.dialogHeader}>
            <div className={classes.headerTitle}>{areaName}</div>
            <div className={classes.headerSubtitle}>
              {isEditMode ? (
                <>
                  You are <u>EDITING</u> {getPropertyAddress() || "this property"}
                </>
              ) : (
                "You are MANUALLY ADDING a Property to the table"
              )}
            </div>
          </div>
        </MailProDialogHeader>
        <MailProDialogBody>
          <div className={classes.dialogBody}>
            <div className={classes.tableContainer}>
              <table className={classes.table}>
                <thead className={classes.tableHeader}>
                  <tr>
                    <th className="status-th">Status</th>
                    <th className="list-sale-date-th">
                      <p>Sale</p>
                      <p>Date</p>
                    </th>
                    <th className="property-type-th">
                      <p>Property</p>
                      <p>Type</p>
                    </th>
                    <th className="address-th">Address</th>
                    <th className="list-price-th">List Price</th>
                    <th className="sale-price-th">Sale Price</th>
                    <th>
                      <p>Days on</p>
                      <p>Market</p>
                    </th>
                    <th>
                      <p>Beds</p>
                    </th>
                    <th>
                      <p>Full Baths /</p>
                      <p>Half Baths</p>
                    </th>
                    <th>
                      <p>Living</p>
                      <p>SqFt</p>
                    </th>
                  </tr>
                </thead>
                <tbody className={classes.tableBody}>
                  {/* First row: Old data (only in edit mode) */}
                  {isEditMode && property && (
                    <tr className={classes.oldDataRow}>
                      {memoizedOldRow}
                    </tr>
                  )}
                  {/* Second row: Form fields */}
                  <tr className={classes.formRow}>
                    <td>
                      <div
                        className={`${classes.chipRow} ${err("status") ? classes.chipRowOutlineError : ""}`}
                      >
                        {STATUS_OPTIONS.map((opt) => {
                          const selected = formData.status === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={`${classes.chip} ${classes.statusChip}`}
                              style={{
                                color: selected ? "white" : opt.text,
                                backgroundColor: selected ? opt.selectedBg : "white",
                                borderColor: opt.selectedBg,
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleInputChange("status", opt.value);
                              }}
                            >
                              {opt.value === "Coming Soon" ? (
                                <span className={classes.comingSoonLabel}>
                                  Coming Soon
                                </span>
                              ) : (
                                opt.label
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {submitAttempted && fieldErrors.status && (
                        <div className={classes.addressErrorMsg}>{fieldErrors.status}</div>
                      )}
                    </td>
                    <td>
                      <input
                        type="text"
                        className={`${classes.formInput} ${err("saleDate") ? classes.formInputError : ""}`}
                        value={formatDateForTextBox(formData.saleDate)}
                        onChange={() => {}}
                        onClick={(e) => {
                          if (formData.status === "Sold" && saleDateInputRef.current?.showPicker) {
                            saleDateInputRef.current.showPicker();
                          }
                        }}
                        placeholder="Select Date"
                        title="Select Date"
                        disabled={formData.status !== "Sold"}
                        readOnly
                      />
                      <input
                        type="date"
                        ref={saleDateInputRef}
                        value={formData.saleDate || ""}
                        onChange={(e) => handleInputChange("saleDate", e.target.value)}
                        max={getSaleDateMaxForInput()}
                        style={{
                          position: "absolute",
                          width: 0,
                          height: 0,
                          opacity: 0,
                          pointerEvents: "none",
                        }}
                        tabIndex={-1}
                        aria-hidden="true"
                      />
                      {submitAttempted && fieldErrors.saleDate && (
                        <div className={classes.addressErrorMsg}>{fieldErrors.saleDate}</div>
                      )}
                    </td>
                    <td>
                      <div
                        className={`${classes.chipRow} ${err("propertyType") ? classes.chipRowOutlineError : ""}`}
                      >
                        {getPropertyTypeOptions().map((opt) => (
                          <button
                            key={opt.key}
                            type="button"
                            className={classes.chip}
                            style={{
                              backgroundColor: formData.propertyType === opt.key ? "#c7ddf8" : "white",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setFormData((prev) => ({
                                ...prev,
                                propertyType: opt.key,
                                customPropertyType: "",
                              }));
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        className={`${classes.formInput} ${err("propertyType") ? classes.formInputError : ""}`}
                        value={formData.customPropertyType ?? ""}
                        onChange={(e) => {
                          const nextCustomType = (e.target.value || "").slice(0, CUSTOM_PROPERTY_TYPE_MAX_LENGTH);
                          setFormData((prev) => ({
                            ...prev,
                            customPropertyType: nextCustomType,
                            propertyType: "",
                          }));
                        }}
                        placeholder="Enter Custom Type"
                        maxLength={CUSTOM_PROPERTY_TYPE_MAX_LENGTH}
                        style={{ marginTop: "6px", width: "100%" }}
                      />
                      <div className={classes.addressCounter}>
                        Custom Type: {customPropertyTypeLength}/{CUSTOM_PROPERTY_TYPE_MAX_LENGTH}
                      </div>
                      {submitAttempted && fieldErrors.propertyType && (
                        <div className={classes.addressErrorMsg}>{fieldErrors.propertyType}</div>
                      )}
                    </td>
                    <td>
                      <div className={classes.addressField}>
                        <input
                          type="text"
                          className={`${classes.formInput} ${classes.addressPart} ${streetNumberHasError ? classes.formInputError : ""}`}
                          value={formData.streetNumber ?? ""}
                          onChange={(e) => {
                            if (isStreetNumberReadOnly(formData.status, streetNumberOverride, formData.isAgentFiltered)) {
                              setShowStreetNumberOverrideDialog(true);
                              return;
                            }
                            handleInputChange("streetNumber", e.target.value);
                          }}
                          onClick={() => {
                            if (isStreetNumberReadOnly(formData.status, streetNumberOverride, formData.isAgentFiltered)) {
                              setShowStreetNumberOverrideDialog(true);
                            }
                          }}
                          placeholder="Enter St #"
                          title="Street number"
                          readOnly={isStreetNumberReadOnly(formData.status, streetNumberOverride, formData.isAgentFiltered)}
                        />
                        <input
                          type="text"
                          className={`${classes.formInput} ${classes.addressPartWide} ${streetNameHasError ? classes.formInputError : ""}`}
                          value={formData.streetName ?? ""}
                          onChange={(e) => handleInputChange("streetName", e.target.value)}
                          placeholder="Enter Street Name"
                          title="Street name"
                        />
                      </div>
                      <div
                        className={`${classes.addressCounter} ${addressOverLimit || err("addressCombined") ? classes.addressCounterError : ""}`}
                      >
                        {addressUsageMessage}
                      </div>
                      <div className={classes.addressPreview}>
                        <span className={classes.addressPreviewLabel}>Full Address:</span>
                        <span className={classes.addressPreviewValue}>{fullAddressCombined || "-"}</span>
                      </div>
                      {submitAttempted && fieldErrors.streetName && (
                        <div className={classes.addressErrorMsg}>
                          {fieldErrors.streetName}
                        </div>
                      )}
                      {submitAttempted && fieldErrors.addressCombined && (
                        <div className={classes.addressErrorMsg}>{fieldErrors.addressCombined}</div>
                      )}
                    </td>
                    <td>
                      <div
                        className={`${classes.priceWrapper} ${err("listPrice") ? classes.priceWrapperError : ""}`}
                      >
                        <span className={classes.pricePrefix}>$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className={classes.formInput}
                          value={formData.listPrice ? Number(String(formData.listPrice).replace(/,/g, "")).toLocaleString() : ""}
                          onChange={(e) =>
                            handleInputChange(
                              "listPrice",
                              sanitizeListSalePriceDigits(e.target.value.replace(/\D/g, ""))
                            )
                          }
                          placeholder="List Price"
                        />
                      </div>
                      {submitAttempted && fieldErrors.listPrice && (
                        <div className={classes.addressErrorMsg}>{fieldErrors.listPrice}</div>
                      )}
                    </td>
                    <td>
                      <div
                        className={`${classes.priceWrapper} ${err("salePrice") ? classes.priceWrapperError : ""}`}
                      >
                        <span className={classes.pricePrefix}>$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className={classes.formInput}
                          value={formData.salePrice ? Number(String(formData.salePrice).replace(/,/g, "")).toLocaleString() : ""}
                          onChange={(e) =>
                            handleInputChange(
                              "salePrice",
                              sanitizeListSalePriceDigits(e.target.value.replace(/\D/g, ""))
                            )
                          }
                          placeholder="Sale Price"
                          disabled={formData.status !== "Sold"}
                        />
                      </div>
                      {submitAttempted && fieldErrors.salePrice && (
                        <div className={classes.addressErrorMsg}>{fieldErrors.salePrice}</div>
                      )}
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={`${classes.smallInput} ${err("daysOnMarket") ? classes.smallInputError : ""}`}
                        value={formData.daysOnMarket ? Number(String(formData.daysOnMarket).replace(/,/g, "")).toLocaleString() : ""}
                        onChange={(e) => handleInputChange("daysOnMarket", e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder="Days"
                        disabled={formData.status === "Coming Soon"}
                      />
                      {submitAttempted && fieldErrors.daysOnMarket && (
                        <div className={classes.addressErrorMsg}>{fieldErrors.daysOnMarket}</div>
                      )}
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={`${classes.smallInput} ${err("beds") ? classes.smallInputError : ""}`}
                        value={formData.beds ?? ""}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
                          if (digits === "") {
                            handleInputChange("beds", "");
                            return;
                          }
                          handleInputChange("beds", String(clampBedsFullHalfInt(digits)));
                        }}
                        placeholder="Beds"
                      />
                      {submitAttempted && fieldErrors.beds && (
                        <div className={classes.addressErrorMsg}>{fieldErrors.beds}</div>
                      )}
                    </td>
                    <td>
                      <div
                        className={`${classes.bathRow} ${bathHasError ? classes.bathRowError : ""}`}
                      >
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formData.fullBath ?? ""}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            if (digits === "") {
                              handleInputChange("fullBath", "");
                              return;
                            }
                            let n = parseInt(digits, 10);
                            if (Number.isNaN(n)) return;
                            if (n > 99) n = 99;
                            handleInputChange("fullBath", String(n));
                          }}
                          aria-label="Full baths"
                        />
                        <span>/</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={
                            formData.halfBath === "" || formData.halfBath === undefined
                              ? "0"
                              : formData.halfBath
                          }
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            if (digits === "") {
                              handleInputChange("halfBath", "0");
                              return;
                            }
                            handleInputChange("halfBath", String(clampBedsFullHalfInt(digits)));
                          }}
                          aria-label="Half baths"
                        />
                      </div>
                      {submitAttempted && (fieldErrors.fullBath || fieldErrors.halfBath) && (
                        <div className={classes.addressErrorMsg}>
                          {fieldErrors.fullBath || fieldErrors.halfBath}
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={`${classes.smallInput} ${err("livingSqft") ? classes.smallInputError : ""}`}
                        value={formData.livingSqft ? Number(String(formData.livingSqft).replace(/,/g, "")).toLocaleString() : ""}
                        onChange={(e) => handleInputChange("livingSqft", e.target.value.replace(/\D/g, "").slice(0, 5))}
                        placeholder="SqFt"
                      />
                      {submitAttempted && fieldErrors.livingSqft && (
                        <div className={classes.addressErrorMsg}>{fieldErrors.livingSqft}</div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Optional: YOUR listing (isAgentFiltered true) / CO-LISTING (isAgentFiltered false) */}
            <div className={classes.optionalButtonsRow}>
              <button
                type="button"
                className={`${classes.optionalButton} ${formData.isAgentFiltered ? classes.optionalButtonSelected : ""}`}
                onClick={handleYourListingClick}
              >
                <strong>Click here if this is YOUR listing</strong>
              </button>
              <button
                type="button"
                className={`${classes.optionalButton} ${!formData.isAgentFiltered ? classes.optionalButtonSelected : ""}`}
                onClick={handleCoListingClick}
              >
                Click here if you are CO-LISTING this property
              </button>
            </div>
            <div className={classes.optionalNote} style={{ marginTop: "8px" }}>
              Note: For ACTIVE or PENDING properties, click Street Number and approve override to edit it. After override it will be visible in Mini Table.
              <br />
              For other status like Sold, Coming Soon, Street Number can be edited.
            </div>
          </div>
        </MailProDialogBody>
        <div className={classes.footerButtons}>
          <MailProButton
            variant="outlined"
            label="Cancel"
            onClick={handleCancel}
            disabled={isSaving}
            customStyle={{
              fontSize: "12px",
              padding: "6px 14px",
              textTransform: "uppercase",
              fontWeight: "bold",
              minWidth: "auto",
              borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
              minHeight: DIALOG_BUTTON_MIN_HEIGHT,
              height: DIALOG_BUTTON_MIN_HEIGHT,
              boxSizing: "border-box",
            }}
          />
          <div className={classes.footerButtonsCenter}>
            <MailProButton
              variant="filled"
              label={isSaving ? "Saving..." : "Update"}
              onClick={handleSave}
              disabled={isSaving}
              customStyle={{
                fontSize: "12px",
                padding: "6px 14px",
                textTransform: "uppercase",
                fontWeight: "bold",
                minWidth: "auto",
                borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                height: DIALOG_BUTTON_MIN_HEIGHT,
                boxSizing: "border-box",
              }}
            />
          </div>
          <div></div>
        </div>
      </MailProDialog>
    </>
  );
}

export default compose(withSnackbar)(SinglePropertyAddEditDialog);
